import { z } from 'zod';

// Fails the build/startup loudly on a missing var instead of shipping
// `undefined` into a gateway URL or an AES key (see the plan's §1 — that
// silent-blank-page failure mode is the thing this exists to close).
const envSchema = z
  .object({
    // Only Razorpay (India) reads this — see RazorpayAdapter.tsx. Thailand's
    // env files don't set it, so it can't be unconditionally required; the
    // superRefine below enforces it just for VITE_COUNTRY=IN.
    VITE_RAZORPAY_KEY: z.string().optional(),
    VITE_SECRET_KEY: z.string().min(1, 'VITE_SECRET_KEY is required'),
    VITE_API_BASE_URL: z.string().url(),
    VITE_PAYMENT_BASE_URL: z.string().url(),
    VITE_PUBLIC_URL: z.string().url(),
    VITE_COUNTRY: z.enum(['IN', 'TH', 'LK']).optional(),
    // Optional on purpose — not every deployment has a support line yet. The
    // "Contact support" action only renders when this is set; see
    // PaymentErrorScreen.tsx.
    VITE_SUPPORT_PHONE: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.VITE_COUNTRY === 'IN' && !data.VITE_RAZORPAY_KEY) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['VITE_RAZORPAY_KEY'],
        message: 'VITE_RAZORPAY_KEY is required when VITE_COUNTRY=IN',
      });
    }
  });

function loadEnv() {
  const parsed = envSchema.safeParse(import.meta.env);

  if (!parsed.success) {
    const issues = parsed.error.issues
      .map((issue) => `  - ${issue.path.join('.')}: ${issue.message}`)
      .join('\n');

    throw new Error(
      `Invalid environment configuration:\n${issues}\n\nCheck .env against environments/.env.example.`
    );
  }

  return parsed.data;
}

export const env = loadEnv();
